import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { settingsAPI, categoryAPI } from '../services/api';
import Select from 'react-select';
import { useToast } from '../components/Toast';

function Settings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [settings, setSettings] = useState({
    aiModel: 'gemini',
    deepseekModel: 'deepseek-chat',
    voiceProvider: 'webspeech' // Default to browser speech to avoid API configuration issues
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Category management state
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [parentCategory, setParentCategory] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  const [editParentCategory, setEditParentCategory] = useState('');
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/user');
      return;
    }
    fetchSettings();
    fetchCategories();
  }, [user, navigate]);

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.getSettings();
      setSettings(response.data.settings);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getCategoryTree();
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) return;
    
    setCategoryLoading(true);
    try {
      await categoryAPI.createCategory({ 
        name: newCategory.trim(),
        parent: parentCategory || null
      });
      setNewCategory('');
      setParentCategory('');
      fetchCategories();
      showToast('Category created successfully', 'success');
    } catch (error) {
      console.error('Error creating category:', error);
      showToast(error.response?.data?.message || 'Failed to create category', 'error');
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleUpdateCategory = async (id) => {
    if (!editCategoryName.trim()) return;
    
    setCategoryLoading(true);
    try {
      await categoryAPI.updateCategory(id, { 
        name: editCategoryName.trim(),
        parent: editParentCategory || null
      });
      setEditingCategory(null);
      setEditCategoryName('');
      setEditParentCategory('');
      fetchCategories();
      showToast('Category updated successfully', 'success');
    } catch (error) {
      console.error('Error updating category:', error);
      showToast(error.response?.data?.message || 'Failed to update category', 'error');
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    
    setCategoryLoading(true);
    try {
      await categoryAPI.deleteCategory(id);
      fetchCategories();
      showToast('Category deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting category:', error);
      showToast(error.response?.data?.message || 'Failed to delete category', 'error');
    } finally {
      setCategoryLoading(false);
    }
  };

  const startEdit = (category) => {
    setEditingCategory(category._id);
    setEditCategoryName(category.name);
    setEditParentCategory(category.parent?._id || '');
  };

  const cancelEdit = () => {
    setEditingCategory(null);
    setEditCategoryName('');
    setEditParentCategory('');
  };

  const toggleExpand = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const getAllCategoriesFlat = (cats, exclude = null) => {
    let flat = [];
    cats.forEach(cat => {
      if (cat._id !== exclude) {
        flat.push(cat);
        if (cat.children && cat.children.length > 0) {
          flat = flat.concat(getAllCategoriesFlat(cat.children, exclude));
        }
      }
    });
    return flat;
  };

  const renderCategoryTree = (cats, level = 0) => {
    if (!cats || cats.length === 0) return null;

    return cats.map(category => {
      const hasChildren = category.children && category.children.length > 0;
      const isExpanded = expandedCategories[category._id];

      return (
        <div key={category._id} style={{ marginLeft: level * 20 + 'px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              background: level === 0 ? '#f7fafc' : '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              marginBottom: '8px'
            }}
          >
            {editingCategory === category._id ? (
              <>
                <div style={{ flex: 1, display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={editCategoryName}
                    onChange={(e) => setEditCategoryName(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      fontSize: '14px',
                      border: '1px solid #667eea',
                      borderRadius: '4px',
                      outline: 'none'
                    }}
                    autoFocus
                  />
                  <div style={{ minWidth: '200px' }}>
                    <Select
                      value={editParentCategory ? 
                        { value: editParentCategory, label: getAllCategoriesFlat(categories, category._id).find(c => c._id === editParentCategory)?.name } 
                        : null
                      }
                      onChange={(option) => setEditParentCategory(option ? option.value : '')}
                      options={[
                        { value: '', label: 'No Parent (Root)' },
                        ...getAllCategoriesFlat(categories, category._id).map(cat => ({ value: cat._id, label: cat.name }))
                      ]}
                      placeholder="Select parent..."
                      isClearable={true}
                      menuPortalTarget={document.body}
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderColor: '#667eea',
                          borderWidth: '1px',
                          borderRadius: '4px',
                          fontSize: '14px',
                          minHeight: '38px',
                          '&:hover': {
                            borderColor: '#667eea'
                          }
                        }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor: state.isSelected ? '#667eea' : state.isFocused ? '#f7fafc' : 'white',
                          color: state.isSelected ? 'white' : '#2d3748',
                          cursor: 'pointer'
                        }),
                        menuPortal: (base) => ({ ...base, zIndex: 1050 })
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleUpdateCategory(category._id)}
                    disabled={categoryLoading}
                    style={{
                      padding: '6px 12px',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: 'white',
                      background: '#10b981',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: categoryLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    ✓ Save
                  </button>
                  <button
                    onClick={cancelEdit}
                    disabled={categoryLoading}
                    style={{
                      padding: '6px 12px',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#4a5568',
                      background: '#e2e8f0',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: categoryLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    ✕ Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                  {hasChildren && (
                    <button
                      onClick={() => toggleExpand(category._id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '16px',
                        padding: '0 4px'
                      }}
                    >
                      {isExpanded ? '▼' : '▶'}
                    </button>
                  )}
                  <span style={{ 
                    fontSize: '14px', 
                    color: '#2d3748', 
                    fontWeight: level === 0 ? '600' : '500',
                    marginLeft: hasChildren ? '0' : '24px'
                  }}>
                    {category.name}
                    {hasChildren && (
                      <span style={{ 
                        fontSize: '12px', 
                        color: '#718096', 
                        marginLeft: '8px' 
                      }}>
                        ({category.children.length})
                      </span>
                    )}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => startEdit(category)}
                    disabled={categoryLoading}
                    style={{
                      padding: '6px 12px',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#667eea',
                      background: 'white',
                      border: '1px solid #667eea',
                      borderRadius: '4px',
                      cursor: categoryLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => handleDeleteCategory(category._id)}
                    disabled={categoryLoading}
                    style={{
                      padding: '6px 12px',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: 'white',
                      background: '#ef4444',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: categoryLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </>
            )}
          </div>
          {hasChildren && isExpanded && renderCategoryTree(category.children, level + 1)}
        </div>
      );
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.updateSettings(settings);
      showToast('Settings saved successfully', 'success');
    } catch (error) {
      console.error('Error saving settings:', error);
      showToast('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div className="spinner-border" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
      <div className="card" style={{ padding: '32px' }}>
        <h2 style={{ color: '#667eea', fontSize: '28px', marginBottom: '24px' }}>
          ⚙️ Admin Settings
        </h2>

        {/* AI Model Selection */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '20px', color: '#2d3748', margin: 0 }}>
              🤖 AI Model Configuration
            </h3>
            <div style={{
              padding: '8px 16px',
              background: settings.aiModel === 'gemini' ? '#dbeafe' : '#fef3c7',
              borderRadius: '20px',
              border: `1px solid ${settings.aiModel === 'gemini' ? '#3b82f6' : '#f59e0b'}`
            }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: settings.aiModel === 'gemini' ? '#1e40af' : '#92400e' }}>
                ⭐ Default: {settings.aiModel === 'gemini' ? 'Google Gemini' : 'Deepseek AI'}
              </span>
            </div>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#4a5568', marginBottom: '8px' }}>
              Select AI Model
            </label>
            <select
              value={settings.aiModel}
              onChange={(e) => setSettings({ ...settings, aiModel: e.target.value })}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '15px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                outline: 'none'
              }}
            >
              <option value="gemini">Google Gemini</option>
              <option value="deepseek">Deepseek AI</option>
            </select>
          </div>

          {/* Gemini Configuration */}
          {settings.aiModel === 'gemini' && (
            <div style={{ 
              padding: '20px', 
              background: '#f7fafc', 
              borderRadius: '8px', 
              border: '1px solid #e2e8f0',
              marginTop: '16px'
            }}>
              <h4 style={{ fontSize: '16px', color: '#667eea', marginBottom: '12px' }}>
                Google Gemini Configuration
              </h4>
              <p style={{ fontSize: '13px', color: '#718096', marginBottom: '0' }}>
                Model: <strong>gemini-2.5-flash-image</strong><br/>
                API Key configured in environment variables (.env file)
              </p>
            </div>
          )}

          {/* Deepseek Configuration */}
          {settings.aiModel === 'deepseek' && (
            <div style={{ 
              padding: '20px', 
              background: '#f7fafc', 
              borderRadius: '8px', 
              border: '1px solid #e2e8f0',
              marginTop: '16px'
            }}>
              <h4 style={{ fontSize: '16px', color: '#667eea', marginBottom: '12px' }}>
                Deepseek AI Configuration
              </h4>
              <p style={{ fontSize: '13px', color: '#718096', marginBottom: '16px' }}>
                API Key configured in environment variables (.env file)
              </p>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#4a5568', marginBottom: '8px' }}>
                  Model Name
                </label>
                <input
                  type="text"
                  value={settings.deepseekModel}
                  onChange={(e) => setSettings({ ...settings, deepseekModel: e.target.value })}
                  placeholder="deepseek-chat"
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '15px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    outline: 'none'
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Voice Feature Configuration */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '20px', color: '#2d3748', margin: 0 }}>
              🔊 Voice Feature Configuration
            </h3>
            <div style={{
              padding: '8px 16px',
              background: settings.voiceProvider === 'elevenlabs' ? '#faf5ff' : '#dbeafe',
              borderRadius: '20px',
              border: `1px solid ${settings.voiceProvider === 'elevenlabs' ? '#9f7aea' : '#3b82f6'}`
            }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: settings.voiceProvider === 'elevenlabs' ? '#6b46c1' : '#1e40af' }}>
                {settings.voiceProvider === 'elevenlabs' ? '⭐ AI Voice Cloning' : '🤖 Browser Voice'}
              </span>
            </div>
          </div>
          
          {/* Global Setting Notice */}
          <div style={{
            padding: '12px 16px',
            background: '#fffbeb',
            border: '1px solid #fbbf24',
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span style={{ fontSize: '18px' }}>👥</span>
            <p style={{ 
              margin: 0, 
              fontSize: '13px', 
              color: '#92400e',
              fontWeight: 500 
            }}>
              This setting applies globally to all interviews and users in the system.
            </p>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#4a5568', marginBottom: '8px' }}>
              Select Voice Provider (Admin Setting)
            </label>
            <select
              value={settings.voiceProvider}
              onChange={(e) => setSettings({ ...settings, voiceProvider: e.target.value })}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '15px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                outline: 'none'
              }}
            >
              <option value="webspeech">Web Speech API (Browser Built-in) - Recommended Default</option>
              <option value="elevenlabs">ElevenLabs AI (Professional Voice Cloning) - Requires API Key</option>
            </select>
          </div>

          {/* ElevenLabs Info */}
          {settings.voiceProvider === 'elevenlabs' && (
            <div style={{ 
              padding: '20px', 
              background: '#faf5ff', 
              borderRadius: '8px', 
              border: '1px solid #e9d5ff',
              marginTop: '16px'
            }}>
              <h4 style={{ fontSize: '16px', color: '#9f7aea', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>✨</span> ElevenLabs Voice Cloning
              </h4>
              <ul style={{ fontSize: '13px', color: '#6b46c1', marginBottom: '0', paddingLeft: '20px' }}>
                <li>Natural, human-like voice quality</li>
                <li>Supports custom voice cloning (use your own voice)</li>
                <li>Multiple languages and accents</li>
                <li>Professional audio for production use</li>
                <li>Free tier: 10,000 characters/month</li>
              </ul>
              <div style={{ 
                marginTop: '12px', 
                padding: '12px', 
                background: 'white', 
                borderRadius: '6px',
                border: '1px solid #e9d5ff'
              }}>
                <p style={{ fontSize: '12px', color: '#6b46c1', margin: '0 0 8px 0', fontWeight: 600 }}>
                  📝 Required Setup:
                </p>
                <p style={{ fontSize: '12px', color: '#6b46c1', margin: 0 }}>
                  Add <code style={{ background: '#faf5ff', padding: '2px 6px', borderRadius: '4px' }}>ELEVENLABS_API_KEY</code> and{' '}
                  <code style={{ background: '#faf5ff', padding: '2px 6px', borderRadius: '4px' }}>ELEVENLABS_VOICE_ID</code> to backend/.env file
                </p>
              </div>
              <div style={{ 
                marginTop: '12px', 
                padding: '12px', 
                background: '#fef3c7', 
                borderRadius: '6px',
                border: '1px solid #fbbf24'
              }}>
                <p style={{ fontSize: '12px', color: '#92400e', margin: 0, fontWeight: 600 }}>
                  ⚠️ Without proper API configuration, interviews will automatically fall back to Browser Voice
                </p>
              </div>
            </div>
          )}

          {/* Web Speech API Info */}
          {settings.voiceProvider === 'webspeech' && (
            <div style={{ 
              padding: '20px', 
              background: '#f0f9ff', 
              borderRadius: '8px', 
              border: '1px solid #bae6fd',
              marginTop: '16px'
            }}>
              <h4 style={{ fontSize: '16px', color: '#3b82f6', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>🌐</span> Web Speech API (Recommended Default)
              </h4>
              <ul style={{ fontSize: '13px', color: '#1e40af', marginBottom: '0', paddingLeft: '20px' }}>
                <li>Built into modern web browsers (Chrome, Edge, Safari)</li>
                <li>No API key or external service required</li>
                <li>Free and unlimited usage</li>
                <li>Zero configuration - works immediately</li>
                <li>Robotic voice quality (functional but less natural)</li>
                <li>Browser-dependent voice options</li>
              </ul>
              <div style={{ 
                marginTop: '12px', 
                padding: '12px', 
                background: 'white', 
                borderRadius: '6px',
                border: '1px solid #bae6fd'
              }}>
                <p style={{ fontSize: '12px', color: '#1e40af', margin: 0, fontWeight: 600 }}>
                  ✅ Perfect for testing and development - no setup required!
                </p>
              </div>
              <div style={{ 
                marginTop: '12px', 
                padding: '12px', 
                background: '#dbeafe', 
                borderRadius: '6px',
                border: '1px solid #93c5fd'
              }}>
                <p style={{ fontSize: '12px', color: '#1e40af', margin: 0, fontWeight: 600 }}>
                  💡 Tip: Upgrade to ElevenLabs for professional voice quality in production
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Category Management */}
        <div style={{ marginBottom: '32px', paddingTop: '32px', borderTop: '2px solid #e2e8f0' }}>
          <h3 style={{ fontSize: '20px', color: '#2d3748', marginBottom: '16px' }}>
            📂 Category Management
          </h3>
          
          {/* Add New Category */}
          <form onSubmit={handleCreateCategory} style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <input
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="Enter new category name"
                style={{
                  flex: 1,
                  minWidth: '200px',
                  padding: '12px',
                  fontSize: '14px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  outline: 'none'
                }}
              />
              <Select
                value={parentCategory ? { value: parentCategory, label: getAllCategoriesFlat(categories).find(c => c._id === parentCategory)?.name } : null}
                onChange={(option) => setParentCategory(option ? option.value : '')}
                options={[
                  { value: '', label: 'No Parent (Root)' },
                  ...getAllCategoriesFlat(categories).map(cat => ({ value: cat._id, label: cat.name }))
                ]}
                placeholder="Select parent category..."
                isClearable={true}
                menuPortalTarget={document.body}
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: '#e2e8f0',
                    borderWidth: '2px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    minWidth: '220px',
                    '&:hover': {
                      borderColor: '#667eea'
                    }
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected ? '#667eea' : state.isFocused ? '#f7fafc' : 'white',
                    color: state.isSelected ? 'white' : '#2d3748',
                    cursor: 'pointer'
                  }),
                  menuPortal: (base) => ({ ...base, zIndex: 1050 })
                }}
              />
              <button
                type="submit"
                disabled={categoryLoading || !newCategory.trim()}
                style={{
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'white',
                  background: categoryLoading || !newCategory.trim() ? '#cbd5e0' : '#667eea',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: categoryLoading || !newCategory.trim() ? 'not-allowed' : 'pointer'
                }}
              >
                ➕ Add Category
              </button>
            </div>
          </form>

          {/* Categories List */}
          <div style={{ 
            maxHeight: '500px', 
            overflowY: 'auto',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '16px'
          }}>
            {categories.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#718096', margin: 0 }}>
                No categories yet. Add your first category above.
              </p>
            ) : (
              <div style={{ display: 'grid', gap: '8px' }}>
                {renderCategoryTree(categories)}
              </div>
            )}
          </div>
        </div>

        {/* Save Button */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={() => navigate('/admin')}
            style={{
              padding: '12px 24px',
              fontSize: '15px',
              fontWeight: 600,
              color: '#4a5568',
              background: '#e2e8f0',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '12px 24px',
              fontSize: '15px',
              fontWeight: 600,
              color: 'white',
              background: saving ? '#cbd5e0' : '#667eea',
              border: 'none',
              borderRadius: '8px',
              cursor: saving ? 'not-allowed' : 'pointer'
            }}
          >
            {saving ? 'Saving...' : '💾 Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Settings;



